import { useState, useEffect } from 'react';
import Hero from './components/Hero';
import ServiceOverview from './components/ServiceOverview';
import Features from './components/Features';
import RentalPlans from './components/RentalPlans';
import UseCases from './components/UseCases';
import Benefits from './components/Benefits';
import FlowSteps from './components/FlowSteps';
import FAQ from './components/FAQ';
import CompanyInfo from './components/CompanyInfo';
import ContactForm from './components/ContactForm';
import Navbar from './components/Navbar';
import SEOJsonLd from './components/SEOJsonLd';

const siteUrl = import.meta.env.VITE_SITE_URL ?? '';

const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name: '合同会社ふらっと',
  description:
    '複合機格安レンタルサービス。初期費用0円・月額定額制で最新のシャープ製カラーレーザー複合機が使えます。保守メンテナンス・消耗品費込み。東京都内の中小企業・店舗・医療機関向け。',
  url: siteUrl,
  telephone: '03-6279-7967',
  foundingDate: '2022-12-01',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '関町北4-31-19',
    addressLocality: '練馬区',
    addressRegion: '東京都',
    postalCode: '177-0051',
    addressCountry: 'JP',
  },
  areaServed: [
    { '@type': 'State', name: '東京都' },
  ],
  priceRange: '¥2〜¥25/枚',
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      opens: '09:00',
      closes: '17:30',
    },
  ],
  employee: {
    '@type': 'Person',
    name: '吉野 均',
    jobTitle: '代表社員',
  },
};

const serviceSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: '複合機格安レンタルサービス',
  serviceType: '複合機レンタル',
  provider: {
    '@type': 'LocalBusiness',
    name: '合同会社ふらっと',
    url: siteUrl,
  },
  description:
    '初期費用0円・月額定額制の複合機レンタルサービス。シャープ製カラーレーザー複合機をモノクロ2円/枚から。保守・トナー代込み。東京都全エリア対応。',
  areaServed: { '@type': 'State', name: '東京都' },
  offers: [
    {
      '@type': 'Offer',
      name: '東京全エリア モノクロ印刷',
      price: '2.1',
      priceCurrency: 'JPY',
      unitText: '枚（税込）',
    },
    {
      '@type': 'Offer',
      name: '東京全エリア カラー印刷',
      price: '13.1',
      priceCurrency: 'JPY',
      unitText: '枚（税込）',
    },
  ],
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '今使っている機械の引取が可能か?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '納品時に限り別途11,000円(税込)にてお引取り可能です。ただし、機種が旧取品でなくリース物件の場合は、事前にお客様からリース会社へ、弊社にて引取り作業を行う承認をお取りいただきます。',
      },
    },
    {
      '@type': 'Question',
      name: '毎月の保守料金には何が含まれているか?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '不具合時の訪問修理費、部品交換費、トナー代が含まれています。紙詰まりなどになりますので、お客様にてご準備が必要です。',
      },
    },
    {
      '@type': 'Question',
      name: 'レンタルする機械は選べるか?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'お客様のご利用環境によってこちらで選定させていただきます。基本的には、現状の利用状況に合う機種、又は将来ご利用の際合格より「年式が新しく」、「スペックを下げない」という条件で機器選定致します。',
      },
    },
    {
      '@type': 'Question',
      name: 'アフターサポートはどのような対応か?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'シャープ・富士フイルム・京セラのメーカー限定設定を持った協力会社のサービスマンが保守対応をさせていただきます。受付時間：平日・土曜9:00〜17:30（日曜・祝日・年末年始は休業）',
      },
    },
    {
      '@type': 'Question',
      name: '申込から納品までの期間はどのくらいか?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '正式ご注文後、1週間〜10日程でお届けが可能です。在庫状況によっては2週間程度お時間いただく場合もございます。',
      },
    },
    {
      '@type': 'Question',
      name: 'Macのパソコンには対応しているか?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '対応機種が限られているため、ヒアリングした上で対応可能モデルをご案内致します。スキャニングについては、別途USBメモリー・NASサーバーをご用意いただく必要がございます。',
      },
    },
    {
      '@type': 'Question',
      name: '設置数年後、機械が古くなった場合は?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '故障頻度が増え修理が不可能な場合は、無料で機械をお入替え致します。お客様都合でのお入替えは再度契約し直しとなります。',
      },
    },
    {
      '@type': 'Question',
      name: '途中解約した場合の違約金はかかるか?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '設置から1年未満での解約の際には、【最低基本料金4,400円(税込)×残期間分】を違約金として頂戴します。',
      },
    },
    {
      '@type': 'Question',
      name: '事務所の移転で保守エリアが変更になった際は、保守料金も変更になるか?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '移転先の保守料金に変更となります。',
      },
    },
  ],
};

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '合同会社ふらっと 複合機レンタル',
  url: siteUrl,
  description: '東京都内の複合機格安レンタルなら合同会社ふらっと。初期費用0円・月額定額制でシャープ製カラーレーザー複合機をモノクロ2円/枚から。',
  inLanguage: 'ja',
  publisher: {
    '@type': 'Organization',
    name: '合同会社ふらっと',
    url: siteUrl,
    address: {
      '@type': 'PostalAddress',
      streetAddress: '関町北4-31-19',
      addressLocality: '練馬区',
      addressRegion: '東京都',
      postalCode: '177-0051',
      addressCountry: 'JP',
    },
  },
};

const webpageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: '複合機格安レンタル【東京】初期費用0円・月額定額制｜合同会社ふらっと',
  url: siteUrl,
  description:
    '東京都内の複合機格安レンタルなら合同会社ふらっと。初期費用0円・月額定額制でシャープ製カラーレーザー複合機をモノクロ2円/枚から。保守メンテナンス・トナー代込み。中小企業・店舗・医療機関に最適。',
  inLanguage: 'ja',
  isPartOf: {
    '@type': 'WebSite',
    url: siteUrl,
  },
  about: {
    '@type': 'Service',
    name: '複合機格安レンタルサービス',
  },
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'ホーム',
        item: siteUrl,
      },
    ],
  },
  dateModified: '2026-04-15',
};

export default function Home() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SEOJsonLd schema={localBusinessSchema} id="schema-local-business" />
      <SEOJsonLd schema={serviceSchema} id="schema-service" />
      <SEOJsonLd schema={faqSchema} id="schema-faq" />
      <SEOJsonLd schema={websiteSchema} id="schema-website" />
      <SEOJsonLd schema={webpageSchema} id="schema-webpage" />
      <Navbar isScrolled={isScrolled} />
      <Hero />
      <ServiceOverview />
      <Features />
      <RentalPlans />
      <UseCases />
      <Benefits />
      <FlowSteps />
      <FAQ />
      <CompanyInfo />
      <ContactForm />
    </div>
  );
}
